import { z } from "zod";

// --- Shared nested schemas ---

const disciplineSchema = z.object({
	id: z.number(),
	type: z.string(),
	deletedAt: z.string().nullable(),
	sortOrder: z.number().nullable(),
});

const buildingLevelSchema = z.object({
	id: z.number(),
	name: z.string(),
	height: z.string(),
	level: z.number(),
	elevation: z.string(),
	isRoof: z.boolean().nullable(),
	thickness: z.string(),
	jobId: z.string().nullable(),
	building: z.object({
		id: z.string(),
	}),
});

const fileSchema = z
	.object({
		id: z.string(),
		path: z.string(),
		key: z.string(),
		name: z.string(),
		size: z.string().nullable(),
		extension: z.string(),
		createdAt: z.string().nullable(),
		createdAtTZ: z.string().nullable(),
		version: z.number().nullable(),
		deletedAt: z.string().nullable(),
		archivedAt: z.string().nullable(),
		isNormalised: z.boolean().nullable(),
		isSliced: z.boolean().nullable(),
		publicUrl: z.string().nullable(),
		forgeUrnPreviewLink: z.string().nullable(),
		localDerivativePath: z.string().nullable(),
		derivativeStorageStatus: z.string().nullable(),
		derivativeTransferredAt: z.string().nullable(),
	})
	.nullable();

const disciplineBuildingLevelFileSchema = z.object({
	id: z.string(),
	file: fileSchema,
	discipline: disciplineSchema,
	buildingLevel: buildingLevelSchema,
});

const inputFormatSchema = z.object({
	id: z.number(),
	parentId: z.number().nullable(),
	type: z.string(),
	description: z.string().nullable(),
	iconName: z.string().nullable(),
	icondata: z.any().nullable(),
	sortOrder: z.number().nullable(),
	parent: z
		.object({
			id: z.number(),
			parentId: z.number().nullable(),
			type: z.string(),
			description: z.string().nullable(),
			iconName: z.string().nullable(),
			icondata: z.any().nullable(),
			sortOrder: z.number().nullable(),
		})
		.nullable()
		.optional(),
});

const jobInputFormatSchema = z.object({
	id: z.string(),
	jobId: z.string(),
	inputFormatId: z.number(),
	inputFormat: inputFormatSchema,
});

const deliveryFormatSchema = z.object({
	id: z.string(),
	deliveryFormat: z.object({
		id: z.number(),
		type: z.string(),
		iconName: z.string().nullable(),
		icondata: z.any().nullable(),
		sortOrder: z.number().nullable(),
		isSupported: z.boolean(),
		parent: z
			.object({
				id: z.number(),
				type: z.string(),
				iconName: z.string().nullable(),
				icondata: z.any().nullable(),
				sortOrder: z.number().nullable(),
				isSupported: z.boolean(),
			})
			.nullable()
			.optional(),
	}),
});

const buildingSchema = z.object({
	id: z.string(),
	orderId: z.string(),
	createdByUserId: z.string().nullable(),
	name: z.string(),
	status: z.string(),
	size: z.string(),
	address: z.any().nullable(),
	levelFrom: z.number(),
	levelTo: z.number(),
	defaultLevelHeight: z.string(),
	additionalInfo: z.string().nullable(),
	createdAt: z.string(),
	createdAtTZ: z.string(),
	deletedAt: z.string().nullable(),
	archivedAt: z.string().nullable(),
	currentStatusSetAt: z.string().nullable(),
	currentStatusSetAtTZ: z.string().nullable(),
	price: z.string(),
	levelsTotal: z.number(),
	uniqueLevels: z.number().nullable(),
	pricePerSquare: z.string(),
	customPricePerSquare: z.string(),
	systemComplexityAdjustmentPercentage: z.string().nullable(),
	systemAdjustedPricePerSquare: z.string(),
	customSystemAdjustedPricePerSquare: z.string(),
	systemAdjustedTotalPrice: z.string().nullable(),
	customComplexityAdjustmentPercentage: z.string().nullable(),
});

const lodSpecificationSchema = z
	.object({
		id: z.number(),
		name: z.string(),
		isPublic: z.boolean(),
		createdAt: z.string(),
		createdAtTZ: z.string(),
		deletedAt: z.string().nullable(),
	})
	.nullable()
	.optional();

// --- Main job response schema ---
// Fields present across all steps
const jobBaseSchema = z.object({
	id: z.string(),
	revitFileId: z.string().nullable(),
	orderId: z.string(),
	serviceTypeId: z.string(),
	supplierCompanyId: z.string().nullable(),
	pointCloudFileId: z.string().nullable(),
	companySubscriptionId: z.string().nullable(),
	supplierCompanySubscriptionId: z.string().nullable(),
	name: z.string(),
	status: z.string(),
	description: z.string().nullable(),
	createdAt: z.string(),
	createdAtTZ: z.string(),
	updatedAt: z.string(),
	updatedAtTZ: z.string(),
	deletedAt: z.string().nullable(),
	archivedAt: z.string().nullable(),
	normalisationDeliveryDate: z.string().nullable(),
	customisationDeliveryDate: z.string().nullable(),
	qaDeliveryDate: z.string().nullable(),
	isViewerEnabled: z.boolean(),
	pricePercentageAdjustment: z.string().nullable(),
	additionalInfo: z.string().nullable(),
	buildingAreaUpdatedSize: z.string().nullable(),
	shouldUseMaverick: z.boolean().nullable(),
	sliceStartHeight: z.string().nullable(),
	sliceThickness: z.string().nullable(),
	shouldDetectLevels: z.boolean().nullable(),

	building: buildingSchema,
	jobInputFormats: z.array(jobInputFormatSchema),
	jobDeliveryFormats: z.array(deliveryFormatSchema),
	lodSpecification: lodSpecificationSchema,

	// Step-dependent: present in step 1, absent in step 2
	revitFile: z.any().optional(),
	serviceType: z.any().optional(),
	jobExports: z.array(z.any()).optional(),

	// Step-dependent: present in step 2, absent in step 1
	jobDisciplineBuildingLevelFiles: z
		.array(disciplineBuildingLevelFileSchema)
		.optional(),
	jobStatusDescriptions: z.array(z.any()).optional(),
	jobJobStatusInputFormatManuals: z.array(z.any()).optional(),
	pointCloudFile: z.any().nullable().optional(),
});

// Passthrough allows extra prodCapacity* fields without listing them all
export const jobResponseSchema = jobBaseSchema.passthrough();

// --- API wrapper schema (envelope) ---

export const jobApiResponseSchema = z.object({
	status: z.boolean(),
	meta: z.object({}).passthrough(),
	result: jobResponseSchema,
});

// --- Exported sub-schemas for reuse ---

export {
	disciplineSchema,
	buildingLevelSchema,
	fileSchema,
	disciplineBuildingLevelFileSchema,
	buildingSchema,
	inputFormatSchema,
};
